import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { OrderApi, PurchaseApi } from "../../../../../../../services/Api/Organization/types";
import { exportDocumentPdf } from "../utils/documentPdf/DocumentPdf";
import { DocumentPdfEntity, ExportDocumentPdfConfig } from "../utils/documentPdf/types";

interface Props {
	show: boolean;
	onHide: () => void;
	entity: DocumentPdfEntity;
	document: PurchaseApi | OrderApi;
	customFieldsConfig: CustomFieldTemplateApi[];
	additionalDate?: string;
}

const DocumentPdfGeneratorModal = ({ onHide, show, entity, document, customFieldsConfig, additionalDate }: Props) => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const [config, setConfig] = useState<ExportDocumentPdfConfig>({
		company: organization.more?.company_name,
		organization: organization.name,
		taxIdNo: organization.tax_id_no,
		printCosts: false,
		printCustomFields: false,
		printPrices: false,
	});

	const handleCheck = (field: string) => {
		setConfig((prevState) => ({
			...config,
			[field]: !prevState[field],
		}));
	};

	const handleGenerate = async () => {
		await exportDocumentPdf(config, entity, t, document.items, document, customFieldsConfig ?? [], additionalDate);
		onHide();
	};

	const handlePrint = async () => {
		await exportDocumentPdf(
			config,
			entity,
			t,
			document.items,
			document,
			customFieldsConfig ?? [],
			additionalDate,
			true
		);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{t("modules.document.field.generate_report_with_data.title")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<h6>{t("modules.document.field.choose_data.title")}</h6>
				<div className="d-flex flex-column">
					{entity === DocumentPdfEntity.ORDER && (
						<Form.Check
							label={t("modules.document.field.show_costs.title")}
							id="printCosts"
							checked={config.printCosts}
							onClick={() => handleCheck("printCosts")}
						/>
					)}
					<Form.Check
						label={t("modules.document.field.show_prices.title")}
						id="printPrices"
						checked={config.printPrices}
						onClick={() => handleCheck("printPrices")}
					/>
					<Form.Check
						label={t("modules.document.field.show_custom_fields.title")}
						id="printCustomFields"
						checked={config.printCustomFields}
						onClick={() => handleCheck("printCustomFields")}
					/>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={handleGenerate}>{t("lib:common.action.generate")}</Button>
				<Button onClick={handlePrint}>{t("lib:common.action.print")}</Button>
				<Button variant="light" onClick={onHide}>
					{t("lib:common.action.cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default DocumentPdfGeneratorModal;
